import type { Opportunity, SalesHistoryPoint } from "./types";

export interface PriceOverride {
  buyPrice?: number;
  sellPrice?: number;
}

export interface TradeEstimate {
  buyPrice: number;
  sellPrice: number;
  profitAbs: number;
  profitPct: number;
}

export interface PeriodMetrics {
  averagePrice: number | null;
  medianPrice: number | null;
  salesCount: number;
  averageDailySales: number;
  volatilityPct: number | null;
  activeDays: number;
  expectedExitDays: number | null;
}

export function calculateTradeEstimate(
  opportunity: Pick<
    Opportunity,
    "buy_price" | "sell_price" | "profit_abs"
  >,
  priceOverride?: PriceOverride,
): TradeEstimate {
  const buyPrice = priceOverride?.buyPrice ?? opportunity.buy_price;
  const sellPrice = priceOverride?.sellPrice ?? opportunity.sell_price;
  const originalNetSale = opportunity.buy_price + opportunity.profit_abs;
  const netSaleRatio =
    opportunity.sell_price > 0
      ? Math.max(0, originalNetSale / opportunity.sell_price)
      : 1;
  const profitAbs = sellPrice * netSaleRatio - buyPrice;

  return {
    buyPrice,
    sellPrice,
    profitAbs,
    profitPct: buyPrice > 0 ? (profitAbs / buyPrice) * 100 : 0,
  };
}

function weightedMedian(
  values: Array<{ value: number; weight: number }>,
): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left.value - right.value);
  const totalWeight = sorted.reduce((sum, item) => sum + item.weight, 0);
  const threshold = totalWeight / 2;
  let cumulative = 0;

  for (const item of sorted) {
    cumulative += item.weight;
    if (cumulative >= threshold) {
      return item.value;
    }
  }

  return sorted.at(-1)?.value ?? null;
}

export function calculatePeriodMetrics(
  points: SalesHistoryPoint[],
  safeQuantity: number,
): PeriodMetrics {
  const active = points.filter(
    (point) => point.avg_price > 0 && point.sales_count > 0,
  );
  const salesCount = active.reduce(
    (sum, point) => sum + point.sales_count,
    0,
  );
  const weightedPriceSum = active.reduce(
    (sum, point) => sum + point.avg_price * point.sales_count,
    0,
  );
  const averagePrice =
    salesCount > 0 ? weightedPriceSum / salesCount : null;
  const medianPrice = weightedMedian(
    active.map((point) => ({
      value: point.avg_price,
      weight: point.sales_count,
    })),
  );

  let volatilityPct: number | null = null;
  if (averagePrice !== null && averagePrice > 0 && active.length > 1) {
    const variance =
      active.reduce(
        (sum, point) => sum + (point.avg_price - averagePrice) ** 2,
        0,
      ) / active.length;
    volatilityPct = (Math.sqrt(variance) / averagePrice) * 100;
  }

  const dailySales = points.length > 0 ? salesCount / points.length : 0;

  return {
    averagePrice,
    medianPrice,
    salesCount,
    averageDailySales: dailySales,
    volatilityPct,
    activeDays: active.length,
    expectedExitDays:
      dailySales > 0 && safeQuantity > 0 ? safeQuantity / dailySales : null,
  };
}
